'use client';
import {
  SectLabel, SectH2, SectLede,
} from '@/components/landing/v4-shared';
import { useV4Enter } from '@/hooks/useV4Enter';

const STEP_COLORS = ['var(--cyan-glow)', 'var(--amber-glow)', 'var(--jade-glow)'];

export function HowItWorksSection() {
  const wrapperRef = useV4Enter();
  const steps = [
    { num: '01', title: 'Sign up', body: 'Create your account and pick your receptionist name and voice. No coding required.' },
    { num: '02', title: 'Forward your number', body: 'Point your business line to your AI receptionist — the only setup step, done in seconds.' },
    { num: '03', title: 'Increase your sales', body: 'Bri answers every call 24/7, qualifies callers, and books the appointments that grow your revenue.' },
  ];

  return (
    <section data-v4-section="how-it-works" aria-labelledby="how-it-works-heading" style={{ background: 'linear-gradient(180deg, rgba(244,244,240,0.015), transparent)' }}>
      <div className="v4-section">
        {/* Centered header */}
        <div style={{ maxWidth: 720, margin: '0 auto 64px', textAlign: 'center' }}>
          <SectLabel color="var(--jade-glow)">How it works</SectLabel>
          {/* Literal question-lead H2 (AEO answer-extractability): kept as plain
              text so the answer is machine-extractable, paired with the 40–60
              word `.speakable-answer` below. */}
          <SectH2 id="how-it-works-heading" style={{ margin: '0 auto 24px' }}>
            How does an AI receptionist work?
          </SectH2>
          <SectLede className="speakable-answer" style={{ margin: '0 auto 0' }}>
            A BizRnR AI receptionist answers 97.8 percent of inbound calls on the first ring, greets callers in a natural voice, follows your qualification script, books appointments into your calendar, and updates your CRM — fully autonomously, 24 hours a day. You go live in three steps in under 60 seconds: sign up, forward your number, and start converting calls.
          </SectLede>
        </div>

        {/* Steps row */}
        <div ref={wrapperRef} style={{ position: 'relative', maxWidth: 1100, margin: '0 auto' }}>
          {/* Gradient connector line behind circles */}
          <div
            className="v4-step-connector"
            style={{
              position: 'absolute',
              top: 36,
              left: '18%',
              right: '18%',
              zIndex: 0,
            }}
          />

          <ol
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 48,
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}
            className="v4-hiw-steps v4-stagger"
          >
            {steps.map((step, i) => (
              <li
                key={step.num}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 16,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {/* Circle */}
                <div
                  aria-hidden="true"
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    border: `2px solid ${STEP_COLORS[i]}`,
                    background: 'var(--ink-2)',
                    boxShadow: `0 0 30px -10px ${STEP_COLORS[i]}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--font-geist-mono)',
                    fontWeight: 500,
                    fontSize: 18,
                    color: STEP_COLORS[i],
                  }}
                >
                  {step.num}
                </div>

                <h3
                  style={{
                    fontFamily: 'var(--font-geist-sans)',
                    fontSize: 24,
                    fontWeight: 500,
                    color: 'var(--paper)',
                    margin: 0,
                  }}
                >
                  {step.title}
                </h3>
                <p
                  style={{
                    fontFamily: 'var(--font-geist-sans)',
                    fontSize: 15,
                    color: 'var(--dim)',
                    lineHeight: 1.55,
                    maxWidth: '30ch',
                    margin: 0,
                  }}
                >
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <style>{`
        @media (max-width: 1100px) {
          .v4-hiw-steps { grid-template-columns: 1fr !important; gap: 40px !important; }
          .v4-step-connector { display: none; }
        }
      `}</style>
    </section>
  );
}

export default HowItWorksSection;
